import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { ExpandMore } from '@mui/icons-material'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { formatDuration } from '../model/durationFormat'
import { catalogsOf, DETAIL_ROWS_SHOWN, groupDetailViewOf, shownDeltaMs, whatOf, workerLabel } from '../model/historyView'
import { comparisonOf } from '../model/comparisonView'
import { useCorrectionLabels } from '../hooks/useCorrectionLabels'
import { parseIsoInstant } from '../model/timeGrid'
import ComparisonTimeline from './ComparisonTimeline'
import { describeBlocker } from '../model/messageCatalog'
import { systemValueCodeLabel } from '../../generic/systemValues/systemValueLabel'
import { messageText } from '../model/messageText'
import { actionOf, revertPlanOf } from '../model/revertPlan'
import { signedDuration, StatusChip, StepChips } from './HistoryList'
import RevertConfirmation from './RevertConfirmation'
import UiMessageAlert from './UiMessageAlert'

const TAPE_LABEL = { JOB_STATE: 'Job state', PRESENCE: 'Presence' }

const Kpi = ({ label, value, note, testId }) => (
  <Box data-testid={testId} sx={{ bgcolor: 'action.hover', borderRadius: 1.5, px: 1.25, py: 1 }}>
    <Typography variant='caption' color='text.secondary' component='div'>{label}</Typography>
    <Typography variant='subtitle1' component='div' sx={{ fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {note && <Typography variant='caption' color='primary' component='div'>{note}</Typography>}
  </Box>
)

// What the panel shows while there is no detail to draw: an invitation, the progress of the request or its failure.
const DetailPlaceholder = ({ selected, onRetry }) => {
  const { t_ } = useNamesTranslation()
  const loading = selected.loading || selected.partner.loading
  return (
    <>
      {selected.message.key && <UiMessageAlert message={selected.message} onAction={onRetry} />}
      {!selected.message.key && loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box>}
      {!selected.message.key && !loading && !selected.id && <Typography color='text.secondary'>{t_('Choose a correction from the list to see its detail.')}</Typography>}
    </>
  )
}

const MARK = ''

/**
 * A message that names corrections (`links`: the later ones of REVERT_LATER_CORRECTIONS_FIRST, the trim of REVERT_CHILD_FIRST):
 * each one as a button with «worker · what · requested on» that opens it (M:965, RN15). A correction id is never shown
 * (PO 2026-10-17); while a header is not known yet, «…».
 */
export const LinkedMessage = ({ message, labelOf, disabled, onOpen }) => {
  const { t_ } = useNamesTranslation()
  const links = message.links || []
  if (links.length === 0) return messageText(message, t_)
  const [before, after] = messageText({ ...message, params: { ...message.params, ids: MARK } }, t_).split(MARK)
  return (
    <>
      {before}
      {links.map((id, index) => (
        <React.Fragment key={id}>
          {index > 0 && ', '}
          <Button
            size='small' variant='text' disabled={disabled} onClick={() => onOpen(id)} data-testid='linked-correction'
            sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline', fontSize: 'inherit' }}
          >
            {labelOf(id) || '…'}
          </Button>
        </React.Fragment>
      ))}
      {after}
    </>
  )
}

/**
 * The actions of the detail (M:1077-1083): the note of the first blocker above the buttons, «Revert…» / «Revert only the trim…»
 * (only with DATA_CORRECTION_REQUEST, RN10), «View on the timeline» (or, when the machine is gone, the text that says so, RN16)
 * and, under them, the inline confirmation of the revert that was opened (RN11).
 */
const DetailActions = ({ view, plan, history, labelOf, onShowInTimeline }) => {
  const { t_ } = useNamesTranslation()
  const { revert, revertReasons, locked } = history
  // The confirmation stays while a reload of the detail changes the plan under it: no error closes it (RN14).
  const known = useRef({})
  ;[plan.primary, plan.secondary].filter(Boolean).forEach((action) => { known.current[action.id] = action })
  const open = revert.action ? actionOf(plan, revert.action) || known.current[revert.action] : undefined
  const note = plan.note ? describeBlocker(plan.note) : undefined
  return (
    <Box>
      {note && (
        <Alert severity='info' sx={{ mb: 1 }} data-testid='revert-blocker-note'>
          <LinkedMessage message={note} labelOf={labelOf} disabled={locked} onOpen={(id) => history.handleSelect(id)} />
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {[plan.primary, plan.secondary].filter(Boolean).map((action) => (
          <Button
            key={action.id} size='small' variant='outlined' color='error' disabled={!action.enabled || locked || revert.action === action.id}
            onClick={() => history.handleOpenRevert(action.id)}
          >
            {t_(action.label)}
          </Button>
        ))}
        {view.deviceExists
          ? <Button size='small' variant='outlined' onClick={() => onShowInTimeline(view.target)}>{t_('View on the timeline')}</Button>
          : <Typography variant='caption' color='text.secondary'>{t_('The machine does not exist.')}</Typography>}
      </Box>
      {open && (
        <RevertConfirmation
          action={open} revert={revert} reasons={revertReasons}
          onReason={history.handleRevertReason} onComment={history.handleRevertComment}
          onConfirm={() => history.handleConfirmRevert(open)} onCancel={history.handleCancelRevert} onRetry={history.handleRetryRevert}
          renderMessage={(message) => <LinkedMessage message={message} labelOf={labelOf} disabled={locked} onOpen={(id) => history.handleSelect(id)} />}
        />
      )}
    </Box>
  )
}

/**
 * The detail of one correction (#205 §8.1, maqueta `.h-detail`): what, who, when and why (and why it was reverted), the
 * KPIs, the records the trace touched before and after (the first 50, then «Show all»: a trace can have thousands of
 * rows) and the history of its states. Story 211: a close and its trim are ONE correction of two steps (RN9), and it can be
 * reverted (RN10-RN15) and seen on the timeline (RN16). `history` is what `useHistory` returns.
 */
const CorrectionDetail = ({ history, canRequest, reasonLabel, onShowInTimeline }) => {
  const { selected, group } = history
  const { t_ } = useNamesTranslation()
  const { formatDateTime } = useDateFormatter()
  const [showAll, setShowAll] = useState(false)
  useEffect(() => setShowAll(false), [selected.id])
  const view = useMemo(
    () => (group.chosen && !selected.partner.loading ? groupDetailViewOf(group, formatDateTime) : undefined),
    [group, selected.partner.loading, formatDateTime]
  )
  const plan = useMemo(() => revertPlanOf(group, canRequest, formatDateTime), [group, canRequest, formatDateTime])
  const comparison = useMemo(() => {
    if (!view) return undefined
    const details = [group.close, group.step || group.chosen].filter(Boolean)
    const catalog = (name) => details.flatMap((detail) => detail[name] || [])
    return comparisonOf({
      details,
      window: (group.step || group.chosen).window,
      names: catalogsOf({ workers: catalog('workers'), jobs: catalog('jobs'), fabricationOrders: catalog('fabricationOrders') }),
      workerId: view.target.workerId,
      nowMs: Date.now()
    })
  }, [view, group])
  // The corrections a note or an error names, by worker, what and date (never by id, PO 2026-10-17).
  const linkedIds = [...((plan.note && describeBlocker(plan.note).links) || []), ...(history.revert.message.links || [])]
  const pageHeaders = useMemo(() => Object.fromEntries(((history.list.response && history.list.response.content) || []).map((header) => [header.id, header])), [history.list.response])
  const headerOf = useCorrectionLabels(linkedIds, pageHeaders)
  const labelOf = (id) => {
    const header = headerOf(id)
    if (!header) return ''
    const what = whatOf(header, formatDateTime)
    return [workerLabel(header.worker), t_(what.key, what.params), t_('requested {{time}}', { time: formatDateTime(parseIsoInstant(header.requestedAt).getTime()) })].join(' · ')
  }

  if (!view) return <DetailPlaceholder selected={selected} onRetry={history.handleRetryDetail} />

  const rows = showAll ? view.rows : view.rows.slice(0, DETAIL_ROWS_SHOWN)
  const where = [view.device, view.fabricationOrder, view.job].filter(Boolean).join(' · ')
  const tapes = view.tapes.map((tape) => t_(TAPE_LABEL[tape] || tape)).join(' · ')
  const rowsNote = [
    t_('{{updated}} modified · {{inserted}} added · {{deleted}} deleted', view.rowsTouched),
    view.rowsOfClose > 0 && t_('{{count}} of the close', { count: view.rowsOfClose })
  ].filter(Boolean).join(' · ')

  return (
    <Box data-testid='correction-detail' sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
      <Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <StatusChip status={view.status} />
          <StepChips steps={view.steps} />
        </Box>
        <Typography variant='h6' component='h2' sx={{ mt: 1 }}>{`${view.worker} · ${t_(view.what.key, view.what.params)}`}</Typography>
        <Typography variant='body2' color='text.secondary'>
          {where}{tapes && ` · ${t_('Corrected tapes: {{tapes}}', { tapes })}`}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {t_('By {{user}} on {{time}} · Reason: {{reason}}', { user: view.requestedBy, time: formatDateTime(view.requestedAtMs), reason: reasonLabel(view.reasonCode) })}
        </Typography>
        {view.requestComment && <Typography variant='body2' sx={{ mt: 0.5 }}>{t_('Comment: {{comment}}', { comment: view.requestComment })}</Typography>}
        {view.reverted && (
          <Box data-testid='revert-info' sx={{ mt: 1, p: 1, borderLeft: 3, borderColor: 'divider' }}>
            <Typography variant='body2'>
              {t_('Reverted by {{user}} on {{time}} · Reason: {{reason}}', { user: view.reverted.by, time: formatDateTime(view.reverted.atMs), reason: reasonLabel(view.reverted.reasonCode) })}
            </Typography>
            {view.reverted.comment && <Typography variant='body2' color='text.secondary'>{t_('Comment: {{comment}}', { comment: view.reverted.comment })}</Typography>}
          </Box>
        )}
      </Box>

      {selected.partner.message.key && <UiMessageAlert message={selected.partner.message} onAction={history.handleRetryPartner} />}
      {view.trimAlreadyReverted && <Alert severity='info'>{t_('The trim of this close is already reverted.')}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1 }}>
        <Kpi
          testId='kpi-stretch' label={t_('Corrected stretch')}
          value={`${formatDateTime(view.stretch.startMs)} – ${formatDateTime(view.stretch.endMs)}`}
          note={formatDuration(view.stretch.durationMs, t_)}
        />
        <Kpi
          testId='kpi-imputed' label={t_('Time imputed to {{worker}}', { worker: view.worker })}
          value={view.imputed.available ? `${formatDuration(view.imputed.beforeMs, t_)} → ${formatDuration(view.imputed.afterMs, t_)}` : t_('not available')}
          note={view.imputed.available ? signedDuration(shownDeltaMs(view.imputed), t_) : ''}
        />
        <Kpi
          testId='kpi-progress' label={t_('Job progress')}
          value={!view.progress.changed ? t_('no change') : view.progress.measured ? `${view.progress.before} → ${view.progress.after}` : t_('not measured by time')}
        />
        <Kpi testId='kpi-rows' label={t_('Records touched')} value={String(view.rowsTouched.total)} note={rowsNote} />
      </Box>

      <DetailActions view={view} plan={plan} history={history} labelOf={labelOf} onShowInTimeline={onShowInTimeline} />

      <ComparisonTimeline comparison={comparison} device={view.device} />

      <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', borderTop: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0, color: 'primary.main' }}>
          <Typography variant='subtitle2'>{t_('Records touched ({{count}})', { count: view.rows.length })}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size='small' stickyHeader aria-label={t_('Before and after of each record')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t_('Record')}</TableCell>
                  <TableCell>{t_('What was recorded')}</TableCell>
                  <TableCell>{t_('After the correction')}</TableCell>
                  <TableCell>{t_('Operation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key} data-testid='detail-row'>
                    <TableCell>
                      {t_(row.table)}
                      {row.closeNow && <Chip size='small' color='info' sx={{ ml: 0.5 }} label={t_('close now')} />}
                      {row.phase === 'REVERT' && <Chip size='small' sx={{ ml: 0.5 }} label={t_('reversion')} />}
                      {row.derived && <Chip size='small' sx={{ ml: 0.5 }} label={t_('calculated by Sun')} />}
                    </TableCell>
                    <TableCell>{row.before || '—'}</TableCell>
                    <TableCell>{row.after || '—'}</TableCell>
                    <TableCell><Typography component='code' variant='caption' sx={{ fontFamily: 'monospace' }}>{systemValueCodeLabel(t_, 'RowOperation', row.operation)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!showAll && view.rows.length > DETAIL_ROWS_SHOWN && (
            <Button size='small' sx={{ mt: 0.5 }} onClick={() => setShowAll(true)}>{t_('Show all ({{count}})', { count: view.rows.length })}</Button>
          )}
        </AccordionDetails>
      </Accordion>

      <Box>
        <Typography variant='subtitle2' sx={{ mb: 0.5 }}>{t_('History of states')}</Typography>
        <Box component='ol' data-testid='status-history' sx={{ m: 0, pl: 2.5 }}>
          {view.history.map((entry) => (
            <Typography component='li' variant='body2' key={entry.key}>
              <b>{t_(entry.label)}</b>{` · ${formatDateTime(entry.atMs)} · ${entry.actor || t_('system')}`}
              {entry.comment && <Typography component='span' variant='body2' color='text.secondary' sx={{ display: 'block' }}>{entry.comment}</Typography>}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default CorrectionDetail
